import React from 'react';
import type { FunctionComponent, ReactNode } from 'react';
import type { UserPermissions } from '@/engine/app/models/CaseState';
import type { AppController } from '@/engine/app/App.controller';

import { observer } from 'mobx-react';

import { useInjection } from 'inversify-react';
import { ConfirmationDialog } from '../../modals/confirmationDialog/ConfirmationDialog';

import styled from 'styled-components';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';

import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import CloudDoneOutlinedIcon from '@mui/icons-material/CloudDoneOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import MoreTimeIcon from '@mui/icons-material/MoreTime';

import { Tooltip, IconButton } from '@mui/material';
import { TYPES } from '@/engine/ioc/types';
import { ModalState } from '../types';
import { UndoRedoButtons } from '../UndoRedoButtons/UndoRedoButtons';
import { ButtonWrapper } from '../ButtonWrapper';
import { IconLabel } from '../IconLabel';
import { getOperationModeTimeInHours } from '@/engine/app/MipEnvValue';
import { SimpleMenuItem } from './SimpleMenuItem';
import { SavingButton } from './SavingButton';
import { useColorMode } from '../useColorMode';
import { QuadroLayoutTopBarButton } from '@/components/layout/quadroLayout/TopbarButton';

export interface IHeaderButtonsProps {
  extraHeaderButtons: Array<FunctionComponent>;
  userPermissions: UserPermissions;
  caseDetailsUrl: string;
  modalStateChangeHandler: (modal: ModalState) => void;
  isCineloopActive: boolean;
  isUserPreferencesAvailable: boolean;
  hasApprovalFunctionality: boolean;
  sessionTimeButtonDisabled: boolean;
}

export const HeaderButtons: React.FC<IHeaderButtonsProps> = observer((props) => {
  const appController = useInjection<AppController>(TYPES.AppController);

  const isApprovalAvailable = props.userPermissions.canApproveCase && props.hasApprovalFunctionality;
  const isApprovalDisabled = props.isCineloopActive;
  const approveButtonToolTip = props.isCineloopActive ? 'Approval is not allowed during cineloop' : '';

  const colorModeProps = useColorMode();

  // session time button
  const sessionTime = getOperationModeTimeInHours();
  const sessionTimeText = props.sessionTimeButtonDisabled
    ? `Session time is extended for ${sessionTime} hours`
    : `Extend session time to ${sessionTime} hours`;

  return (
    <Container>
      <UndoRedoButtons />
      <QuadroLayoutTopBarButton />
      <SimpleMenuItem
        disabled={props.sessionTimeButtonDisabled}
        tooltipText={sessionTimeText}
        onClick={(): void => {
          if (props.sessionTimeButtonDisabled) {
            return;
          }
          props.modalStateChangeHandler(ModalState.SessionTime);
        }}
        title="Session time"
        testId="SessionTimeBtn"
        icon={MoreTimeIcon}
      />
      <SimpleMenuItem
        onClick={(): void => {
          window.open(props.caseDetailsUrl, '_blank');
        }}
        title="Case Details"
        testId="CaseDetailsBtn"
        icon={DescriptionOutlinedIcon}
      />
      <SimpleMenuItem {...colorModeProps} />
      {props.isUserPreferencesAvailable && (
        <ButtonWrapper>
          <IconButton
            size="small"
            data-testid={'PreferencesBtn'}
            onClick={(): void => {
              props.modalStateChangeHandler(ModalState.UserPreferences);
            }}
          >
            <SettingsSuggestIcon color="secondary" fontSize="medium" />
          </IconButton>
          <IconLabel>Preferences</IconLabel>
        </ButtonWrapper>
      )}
      <SavingButton
        userPermissions={props.userPermissions}
        isCineloopActive={props.isCineloopActive}
        saveCase={(): Promise<void> => appController.saveCase()}
      />
      {isApprovalAvailable && (
        <ButtonWrapper disabled={isApprovalDisabled}>
          <Tooltip title={approveButtonToolTip} placement="bottom">
            <div>
              <ConfirmationDialog
                title="Approve Plan"
                primaryActionText={'OK'}
                description="By continuing you confirm that you have validated the presented workflow models, landmarks and measurements on the medical images and want to approve your case using the current device position and size."
                response={(): void => {
                  // eslint-disable-next-line @typescript-eslint/no-floating-promises -- //TODO: [chebotarev] fix eslint no-floating-promises
                  appController.approveCase();
                }}
              >
                {(showDialog): ReactNode => (
                  <IconButton
                    onClick={showDialog}
                    disabled={isApprovalDisabled}
                    data-testid={'ApproveCaseBtn'}
                    size="small"
                  >
                    <CloudDoneOutlinedIcon color="secondary" fontSize="medium" />
                  </IconButton>
                )}
              </ConfirmationDialog>
            </div>
          </Tooltip>
          <IconLabel>Approve</IconLabel>
        </ButtonWrapper>
      )}
      <SimpleMenuItem
        title="Help"
        testId="HelpBtn"
        icon={HelpOutlineOutlinedIcon}
        onClick={(): void => props.modalStateChangeHandler(ModalState.Help)}
      />
      {props.extraHeaderButtons.map((func, i) => React.createElement(func, { key: i }))}
    </Container>
  );
});

export const Container = styled.div`
  display: flex;
  column-gap: 0.7em;
  justify-content: right;
  pointer-events: none;
  margin-top: -0.5em;
  font-size: 0.9em;
`;
